import { Component, computed, inject, input, OnInit, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { AdaaHelper } from "../../../core/utils";
import {
  ConfirmationModalComponent,
  FormActionButtonsComponent,
  FormCheckboxComponent,
  FormInputComponent,
  FormTwoWaySelectComponent,
} from "../../../shared/components";
import { Constants } from "../../../shared/constants/constants";
import { AdaaBoolean, Language, PageMode } from "../../../shared/constants/enums";
import {
  AlphabetOnlyDirective,
  SmartToolTipsDirective,
  SmartToolTipsService,
  UppercaseDirective,
} from "../../../shared/directives";
import { EntityModelType, ValidatorModelType, ValueText } from "../../../shared/models";
import { PillarEntity } from "../../../shared/models/pillar-model";
import { AppService, EntitiesApiService, PillarsApiService, ValidatorApiService } from "../../../shared/services";

@Component({
  selector: "adaa-pillar-editor",
  imports: [
    RouterModule,
    ReactiveFormsModule,
    TranslatePipe,
    FormInputComponent,
    FormTwoWaySelectComponent,
    FormCheckboxComponent,
    FormActionButtonsComponent,
    UppercaseDirective,
    AlphabetOnlyDirective,
    SmartToolTipsDirective,
  ],
  templateUrl: "./pillar-editor.html",
  styleUrl: "./pillar-editor.scss",
})
export class PillarEditorComponent implements OnInit {
  private _formBuilder = inject(FormBuilder);
  private _pillarsApiService = inject(PillarsApiService);
  private _modalService = inject(NgbModal);
  private _appService = inject(AppService);
  private _activatedRoute = inject(ActivatedRoute);
  private _entitiesApiService = inject(EntitiesApiService);
  private _translateService = inject(TranslateService);
  private _toastrService = inject(ToastrService);
  private _router = inject(Router);
  private _smartToolTipsService = inject(SmartToolTipsService);
  private _validatorApiService = inject(ValidatorApiService);

  pillarForm: FormGroup = new FormGroup({});
  pageMode = input.required<string>();

  PageMode = PageMode;
  pillarId: string | null;
  enablerEntities = signal<EntityModelType[]>([]);
  selectedEntities = signal<PillarEntity[]>([]);
  submitted = signal<boolean>(false);
  formValidation = signal<ValidatorModelType | undefined>(undefined);

  isView = computed(() => this.pageMode() === PageMode.view);

  enablerEntityOptions = computed(() =>
    this.enablerEntities().map((entity) => {
      return {
        text: AdaaHelper.getItemValueByToken(entity, "name"),
        value: entity.id,
      };
    })
  );

  public get displayLabel(): string {
    const nameEN = this.pillarForm.get("nameEN")?.value;
    const nameAE = this.pillarForm.get("nameAE")?.value;

    return AdaaHelper.getPageLabel(nameAE, nameEN);
  }

  public ngOnInit(): void {
    this.pillarId = this._activatedRoute.snapshot?.paramMap?.get("id");

    this._getEnablerEntities();
    this._prepareForm();
    if (this.pageMode() !== PageMode.view)
      this._smartToolTipsService.initSmartToolTips(Constants.SMART_TOOL_TIPS_KEY.SMART_PILLAR);
  }

  private _prepareForm() {
    const isViewMode = this.pageMode() === PageMode.view;

    this.pillarForm = this._formBuilder.group({
      refCode: { value: null, disabled: isViewMode },
      nameAE: { value: null, disabled: isViewMode },
      nameEN: { value: null, disabled: isViewMode },
      dscAE: { value: null, disabled: isViewMode },
      dscEN: { value: null, disabled: isViewMode },
      available: { value: null, disabled: isViewMode },
      pillarEntities: { value: [], disabled: isViewMode },
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_PILLARS, this.pillarForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.pillarForm = response.form;
        },
      });
  }

  private _getEnablerEntities() {
    this._entitiesApiService.getEntitiesByEnabler().subscribe({
      next: (response) => {
        this.enablerEntities.set(response.responseData);
      },
      complete: () => this.pillarId && this._getPillar(+this.pillarId),
    });
  }

  private _getPillar(pillarId: number) {
    this._pillarsApiService.getPillarById(pillarId).subscribe({
      next: (response) => {
        this.selectedEntities.set(response.responseData.pillarEntities);
        const mappedPillarEntities = response.responseData.pillarEntities.map((entity) => entity.entityId);
        this.pillarForm.patchValue({ ...response.responseData, pillarEntities: mappedPillarEntities });
        this.pillarForm.markAsPristine();
        this.pillarForm.markAsUntouched();
      },
    });
  }

  public cancelForm() {
    if (this.pillarForm.dirty) {
      const modal = this._modalService.open(ConfirmationModalComponent, {
        centered: true,
        size: "md",
        modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
      });

      modal.componentInstance.header = "common.form.modals_leave.cancel_yes_no_title";
      modal.componentInstance.title = "common.form.modals_leave.cancel_yes_no_information";

      modal.result.then((e) => {
        if (e) this._router.navigateByUrl(`/console/pillars/list`);
      });
    } else this._router.navigateByUrl(`/console/pillars/list`);
  }

  public updatePillar() {
    this.submitted.set(true);
    if (this.pillarForm.invalid) {
      this._toastrService.warning(this._translateService.instant("notification.warning.missing_info"));
      return;
    }
    const pillar = this._formatPillar();

    const serviceUrl =
      this.pageMode() === PageMode.edit
        ? this._pillarsApiService.updatePillar(pillar)
        : this._pillarsApiService.createPillar(pillar);

    serviceUrl.subscribe({
      next: (response) => {
        if (response.inError) return;
        this._toastrService.success(this._translateService.instant("notification.success.save"));
        this._router.navigateByUrl("/console/pillars/list");
      },
    });
  }

  public checkInvalid(control: string) {
    return (
      this.pillarForm.controls[control].errors?.["required"] &&
      (this.pillarForm.get("nameEN")?.dirty || this.submitted())
    );
  }

  private _formatPillar() {
    const pillar = this.pillarForm.value;
    const pillarEntities: number[] = this._extractValues(pillar.pillarEntities);
    const result: Partial<PillarEntity>[] = [];

    this.selectedEntities()?.forEach((selectedEntity) => {
      if (!pillarEntities.includes(selectedEntity.entityId)) {
        selectedEntity.status = Constants.OBJECT_STATUS.REMOVE;
      } else selectedEntity.status = Constants.OBJECT_STATUS.ACTIVE;
      result.push(selectedEntity);
    });

    pillarEntities.forEach((entity) => {
      if (!result.find((item) => item.entityId === entity)) {
        const match = this.enablerEntities().find((enablerEntity) => enablerEntity.id === entity);
        result.push({
          entityId: match?.id,
          entityNameAE: match?.nameAE,
          entityNameEN: match?.nameEN,
          entityShortNameAE: match?.shortNameAE,
          entityShortNameEN: match?.shortNameEN,
          status: Constants.OBJECT_STATUS.ACTIVE,
        });
      }
    });

    pillar.pillarEntities = result;
    pillar.status = Constants.OBJECT_STATUS.ACTIVE;
    pillar.refCode = pillar.refCode?.toUpperCase();
    pillar.available = pillar.available ?? AdaaBoolean.N;
    if (this.pageMode() === PageMode.edit) {
      pillar.updateTS = Date.now();
      pillar.id = this.pillarId;
    }
    return pillar;
  }

  private _extractValues(array: (number | ValueText)[]): number[] {
    const res: number[] = [];
    array.forEach((item) => {
      if (typeof item === "number") {
        res.push(item);
      } else if (typeof item === "object" && "value" in item) {
        res.push(item.value);
      }
    });
    return res;
  }
}
